import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { CareerPath } from '../../../core/models/career-path';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CareerPathService } from '../../../core/services/career-path.service';
import { CareerPathFormComponent } from './career-path-form/career-path-form.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';


@Component({
  selector: 'em-career-path',
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule, ToastModule, ConfirmDialogModule, TranslatePipe, EmPermissionDirective],
  templateUrl: './career-path.component.html',
  styleUrl: './career-path.component.scss',
  providers: [ConfirmationService],
})
export class CareerPathComponent {
  careerPaths!: CareerPath[];
  ref: DynamicDialogRef | undefined;
  translations: any = {};
  Permission = Permission;


  constructor(
    private careerPathService: CareerPathService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.getAllCareerPaths();
    this.loadTranslations();
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.careerPath.deleteCareerPath.message',
        'layout.configurations.careerPath.deleteCareerPath.title',
        'layout.configurations.careerPath.deleteCareerPath.successMessage.title',
        'layout.configurations.careerPath.deleteCareerPath.successMessage.detail',
        'layout.configurations.careerPath.deleteCareerPath.errorMessage.title',
        'layout.configurations.careerPath.deleteCareerPath.errorMessage.detail',
        'layout.configurations.careerPath.deleteCareerPath.infoMessage.title',
        'layout.configurations.careerPath.deleteCareerPath.infoMessage.detail',
        'layout.configurations.careerPath.editCareerPath.title',
        'layout.configurations.careerPath.editCareerPath.successMessage.title',
        'layout.configurations.careerPath.editCareerPath.successMessage.detail',
        'layout.configurations.careerPath.addCareerPath.title',
        'layout.configurations.careerPath.addCareerPath.successMessage.title',
        'layout.configurations.careerPath.addCareerPath.successMessage.detail',
        'layout.configurations.careerPath.deleteCareerPath.yes',
        'layout.configurations.careerPath.deleteCareerPath.no',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  getAllCareerPaths() {
    this.careerPathService.getAll().subscribe((data) => (this.careerPaths = data));
  }

  onAdd() {
    this.ref = this.dialogService.open(CareerPathFormComponent, {
      header: this.translations['layout.configurations.careerPath.addCareerPath.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
    });

    this.ref.onClose.subscribe((path) => {
      if (path) {
        this.getAllCareerPaths();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.careerPath.addCareerPath.successMessage.title'],
          detail: this.translations['layout.configurations.careerPath.addCareerPath.successMessage.detail'],
        });
      }
    });
  }

  onEdit(careerPath: CareerPath) {
    this.ref = this.dialogService.open(CareerPathFormComponent, {
      header: this.translations['layout.configurations.careerPath.editCareerPath.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
      data: careerPath,
    });

    this.ref.onClose.subscribe((result) => {
      if (!result) return;
      if (result.type === 'updated') {
        this.getAllCareerPaths();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.careerPath.editCareerPath.successMessage.title'],
          detail: this.translations['layout.configurations.careerPath.editCareerPath.successMessage.detail'],
        });
      }
      else if (result.type === 'no-change') {
        this.messageService.add({
        severity: 'warn',
        summary:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
        detail:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage')
      });
       }
    });
  }

  onDelete(event: Event, id: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.careerPath.deleteCareerPath.message'],
      icon: 'pi pi-info-circle',
      header: this.translations['layout.configurations.careerPath.deleteCareerPath.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.careerPath.deleteCareerPath.yes'],
      rejectLabel: this.translations['layout.configurations.careerPath.deleteCareerPath.no'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteCareerPath(id);
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translations['layout.configurations.careerPath.deleteCareerPath.infoMessage.title'],
          detail: this.translations['layout.configurations.careerPath.deleteCareerPath.infoMessage.detail'],
          life: 3000,
        });
      },
    });
  }

  deleteCareerPath(id: number) {
    this.careerPathService.deleteCareerPath(id).subscribe({
      next: () => {
        this.getAllCareerPaths();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.careerPath.deleteCareerPath.successMessage.title'],
          detail: this.translations['layout.configurations.careerPath.deleteCareerPath.successMessage.detail'],
        });
      },
      error: (err) => {
        console.log('Error deleting career path: ' + err);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.careerPath.deleteCareerPath.errorMessage.title'],
          detail: this.translations['layout.configurations.careerPath.deleteCareerPath.errorMessage.detail'],
        });
      },
    });
  }
}
